import { observer, Observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  ControlGroup,
  EmptyInputGroup,
  Icon,
  ITagProps,
  Menu,
  MenuItem,
  minorScale,
  NumberInput,
  Popover,
  Tag,
  TextArea,
} from '../../npaper';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { ITask, useStores, Unit, TaskStatus, ITaskLog } from '../../stores';

export const TaskLogTracking = observer(
  ({ task, log }: { task?: ITask; log?: ITaskLog }) => {
    const { taskLogStore, taskStore } = useStores();
    const intl = useIntl();
    const [hours, setHours] = useState<number>();
    const [minutes, setMinutes] = useState<number>();
    const [note, setNote] = useState('');

    const addLog = () => {
      const duration = moment
        .duration(
          ((hours || 0) * 60 + (minutes || 0)) * 60 * 1000,
          'milliseconds'
        )
        .asMilliseconds();
      if (log) {
        // Update the log (no need to update task as it works automatically with MST)
        taskLogStore.updateItem(log, { duration, note });
      } else if (task) {
        // Create a log and refresh task
        taskLogStore
          .createItem({
            task: task.id,
            duration,
            note,
          })
          .then(() => taskStore.getItem(task.id, true));
      }
    };

    const reset = React.useCallback(() => {
      setHours(log ? Math.floor(log.duration / 60 / 60 / 1000) : undefined);
      setMinutes(log ? Math.floor(log.duration / 60 / 1000) % 60 : undefined);
      setNote((log && log.note) || '');
    }, [log]);

    useEffect(() => {
      reset();
    }, [reset, log]);

    return (
      <Popover
        onOpen={() => {
          reset();
        }}
        content={(close) => (
          <Menu>
            <Box paddingX={minorScale(1)}>
              <ControlGroup>
                <NumberInput
                  placeholder="0"
                  value={hours}
                  onChange={setHours}
                  width={120}
                  maximumFractionDigits={0}
                  minimumFractionDigits={0}
                  min={0}
                />
                <EmptyInputGroup>
                  <FormattedUnit unit={Unit.HOUR} />
                </EmptyInputGroup>
                <NumberInput
                  placeholder="0"
                  value={minutes}
                  onChange={(minutes) => {
                    setMinutes(minutes % 60);
                    if (minutes >= 60) {
                      setHours((h) => (h || 0) + Math.floor(minutes / 60));
                    }
                  }}
                  width={120}
                  maximumFractionDigits={0}
                  minimumFractionDigits={0}
                  min={0}
                />
                <EmptyInputGroup>
                  <FormattedUnit unit={Unit.MINUTE} />
                </EmptyInputGroup>
              </ControlGroup>
            </Box>

            <Box paddingX={minorScale(1)}>
              <TextArea
                placeholder={intl.formatMessage({
                  defaultMessage: 'Make a short description of the task',
                })}
                rows={2}
                maxLength={80}
                hideHint
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Box>
            <MenuItem
              text={log ? 'Updater log' : 'Tilføj til log'}
              intent="info"
              icon="stopwatch"
              disabled={!hours && !minutes}
              onClick={() => {
                addLog();
                close();
              }}
            />
          </Menu>
        )}
      >
        <Observer>
          {() => (
            <TaskTimeTag
              duration={(log && log.duration) || (task && task.timeSpent)}
              status={!log && task ? task.status : TaskStatus.CREATED}
            />
          )}
        </Observer>
      </Popover>
    );
  }
);

export const TaskTimeTag = ({
  status,
  duration,
  onClick,
}: {
  status: TaskStatus;
  duration?: number | null;
  onClick?: () => void;
}) => (
  <Tag
    cursor="pointer"
    onClick={onClick}
    intent={
      status === TaskStatus.CREATED
        ? 'none'
        : status === TaskStatus.PROCESSING
        ? 'info'
        : status === TaskStatus.PAUSED
        ? 'warning'
        : 'success'
    }
  >
    <Icon icon="stopwatch" marginRight={minorScale(1)} />
    {duration
      ? moment
          .duration(duration, 'milliseconds')
          .format('hh:mm', { trim: false })
      : '00:00'}
  </Tag>
);

export const TaskStatusTag = ({
  task,
  ...props
}: { task: ITask } & ITagProps) => (
  <Tag
    {...props}
    intent={
      task.status === TaskStatus.CREATED
        ? 'none'
        : task.status === TaskStatus.PROCESSING
        ? 'info'
        : task.status === TaskStatus.PAUSED
        ? 'warning'
        : 'success'
    }
  >
    {task.status === TaskStatus.CREATED ? (
      <FormattedMessage defaultMessage="Created" />
    ) : task.status === TaskStatus.PAUSED ? (
      <FormattedMessage defaultMessage="Paused" />
    ) : task.status === TaskStatus.PROCESSING ? (
      <FormattedMessage defaultMessage="Working on it" />
    ) : task.status === TaskStatus.DONE ? (
      <FormattedMessage defaultMessage="Done" />
    ) : (
      'UNKOWN'
    )}
  </Tag>
);
